import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Grid, Input, Select } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import styled from 'styled-components';

import { GrayBox, ItemTitle } from '@/components/Service/tools';

import { type VolumeItem, VolumeMountType, type VolumeMountsProps } from '../volumeTypes';

const Row = Grid.Row;
const Col = Grid.Col;
const FormList = Form.List;
const FormItem = Form.Item;

const { NotMount, ReadOnly, ReadWrite, ReadWriteMany, ReadWriteOnce, ReadOnlyMany } = VolumeMountType;

interface AddedPodsProps<T extends VolumeMountsProps = VolumeItem> {
  form: FormInstance<T>;
  from?: string;
  type?: 'template' | 'other';
}

export function AddedPods<T extends VolumeMountsProps = VolumeItem>({ form, from, type }: AddedPodsProps<T>) {
  const { t } = useTranslation();
  const volumeMountValues = (form.getFieldValue('volumeMounts') as VolumeMountsProps['volumeMounts']) || [];

  const options = useMemo(() => {
    if (from === 'container-create') {
      return [
        { value: ReadWriteMany, label: ReadWriteMany },
        { value: ReadWriteOnce, label: ReadWriteOnce },
        { value: ReadOnlyMany, label: ReadOnlyMany },
      ];
    }
    return [
      { value: NotMount, label: t('common:do.not.mount') },
      { value: ReadOnly, label: t('common:read.only') },
      { value: ReadWrite, label: t('common:read.write') },
    ];
  }, [from, t]);

  const hideMountType = type === 'template' && from === 'container-create';

  return (
    <PodListBox>
      <FormList field="volumeMounts">
        {(fields) =>
          fields.map((item, index) => (
            <GrayBox key={index}>
              <ItemTitle>{volumeMountValues?.[index]?.containerName}</ItemTitle>
              <Row gutter={8}>
                {!hideMountType ? (
                  <Col span={12}>
                    <FormItem field={`${item.field}.mountType`}>
                      <Select showSearch options={options} getPopupContainer={() => document.body} />
                    </FormItem>
                  </Col>
                ) : null}
                <Col span={hideMountType ? 24 : 12}>
                  <FormItem field={`${item.field}.mountPath`}>
                    <Input placeholder={t('common:mount.path')} />
                  </FormItem>
                </Col>
              </Row>
            </GrayBox>
          ))
        }
      </FormList>
    </PodListBox>
  );
}

const PodListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
